/* ============================================================
   KOS Deployment Tracker — Excel parser (client-side, SheetJS)
   ------------------------------------------------------------
   Maps the "00 Spend.pro" sheet of the KPS Digital Deployment
   Tracker workbook into the record shape consumed by the
   dashboard template (window.KOS_RECORDS).

   Field map (from the sheet):
     client       <- Client
     billability  <- Billability        (Billable / BD / Investment / None)
     hours        <- Billed hours
     days         <- Billed Days
     monthKey     <- Billed year-Month   -> "YYYY-MM"
     code         <- Billing Code
     billedBy     <- Billed By           (resource; may list several)
     scopes[]     <- "Yes" flags across the four scope columns
     status       <- Overal status
     start/commit/compl, kpsLead, kpsTeam, comments
   ============================================================ */
import * as XLSX from "xlsx";

/* Each scope is matched against the (lower-cased) column header.
   Note: the source sheet misspells "visuals" as "visulas", so basic/advanced
   are matched on their qualifier ("basic"/"advanc") rather than "visual". */
export const KOS_SCOPE_DEFS = [
  { key: "Gen AI categorization", test: (h) => h.includes("categoriz") || h.includes("gen ai") || h.includes("genai") },
  { key: "Manual validation", test: (h) => h.includes("manual") || h.includes("valid") },
  { key: "Spend.pro visuals (basic)", test: (h) => h.includes("basic") },
  { key: "Spend.pro visuals (advanced)", test: (h) => h.includes("advanc") },
];

const norm = (s) => String(s == null ? "" : s).replace(/\s+/g, " ").trim();
const low = (s) => norm(s).toLowerCase();

/* ---- month normalization -> "YYYY-MM" ---- */
const MONTH_NAMES = {
  jan: "01", feb: "02", mar: "03", apr: "04", may: "05", jun: "06",
  jul: "07", aug: "08", sep: "09", oct: "10", nov: "11", dec: "12",
};
function toMonthKey(v) {
  if (v == null || v === "") return null;
  // Excel date serial number -> UTC date.
  // Epoch 1899-12-30 absorbs Excel's fictitious 1900 leap day; UTC getters
  // avoid timezone month-shift. Math.round drops any fractional time-of-day.
  if (typeof v === "number" && isFinite(v) && v > 59 && v < 2958466) {
    const d = new Date(Date.UTC(1899, 11, 30) + Math.round(v) * 86400000);
    if (!isNaN(d)) {
      return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    }
  }
  if (v instanceof Date && !isNaN(v)) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, "0")}`;
  }
  const s = norm(v);
  // 2024-04 / 2024/04 / 2024-04-01
  let m = s.match(/^(\d{4})[-/](\d{1,2})/);
  if (m) return `${m[1]}-${String(+m[2]).padStart(2, "0")}`;
  // 04-2024 / 04/2024
  m = s.match(/^(\d{1,2})[-/](\d{4})$/);
  if (m) return `${m[2]}-${String(+m[1]).padStart(2, "0")}`;
  // Apr-24 / Apr 2024 / April-2024
  m = s.match(/^([A-Za-z]{3,})[-\s/]+(\d{2,4})$/);
  if (m) {
    const mm = MONTH_NAMES[m[1].slice(0, 3).toLowerCase()];
    if (mm) {
      let y = m[2];
      if (y.length === 2) y = (+y >= 70 ? "19" : "20") + y;
      return `${y}-${mm}`;
    }
  }
  // last resort: let Date try
  const d = new Date(s);
  if (!isNaN(d) && /\d{4}/.test(s)) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  }
  return null;
}

function toNumber(v) {
  if (v == null || v === "") return 0;
  if (typeof v === "number") return v;
  const n = parseFloat(String(v).replace(/[^0-9.\-]/g, ""));
  return isFinite(n) ? n : 0;
}

const isYes = (v) => {
  const s = low(v);
  return s === "yes" || s === "y" || s === "true" || s === "1" || s === "x" || s === "✓";
};

/* split a "Billed By" cell into individuals */
function splitPeople(v) {
  const s = norm(v);
  if (!s) return [];
  return s
    .split(/[,;/&]|\band\b|\+/i)
    .map(norm)
    .filter(Boolean);
}

/* normalize an "Overal status" cell into the dashboard's 3 buckets.
   Blank / unknown values become "On hold/in-pipeline/Unknown". */
const STATUS_PENDING = "On hold/in-pipeline/Unknown";
function canonStatus(v) {
  const s = low(v);
  if (!s) return STATUS_PENDING;
  if (s.includes("complet") || s === "done" || s === "closed") return "Completed";
  if (s.includes("progress") || s.includes("ongoing") || s === "wip" || s.includes("in-progress"))
    return "In progress";
  if (s.includes("hold") || s.includes("pipeline") || s.includes("unknown")) return STATUS_PENDING;
  return STATUS_PENDING;
}

/* normalize a billability label to one of the known buckets */
function canonBillability(v) {
  const s = low(v);
  if (!s) return "None";
  if (s.includes("billab")) return "Billable";
  if (s === "bd" || s.includes("business dev")) return "BD";
  if (s.includes("invest")) return "Investment";
  if (s === "none" || s === "-" || s === "—") return "None";
  return norm(v) || "None";
}

/* find the column index for a field given the header row cells */
function findCol(headers, predicate) {
  for (let i = 0; i < headers.length; i++) {
    if (predicate(low(headers[i]), i)) return i;
  }
  return -1;
}

/* Locate the header row + the data sheet inside the workbook. */
function pickSheet(wb) {
  const names = wb.SheetNames;
  // prefer a sheet that looks like "00 Spend.pro"
  const preferred = names.find((n) => /spend\.?\s*pro/i.test(n));
  const order = preferred ? [preferred, ...names.filter((n) => n !== preferred)] : names;
  let best = null;
  for (const name of order) {
    const ws = wb.Sheets[name];
    if (!ws) continue;
    const rows = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: "" });
    const hr = findHeaderRow(rows);
    if (hr >= 0) {
      const score = scoreHeader(rows[hr]);
      if (!best || score > best.score) best = { name, rows, headerRow: hr, score };
      if (preferred && name === preferred && score >= 3) break;
    }
  }
  return best;
}

function findHeaderRow(rows) {
  for (let i = 0; i < Math.min(rows.length, 15); i++) {
    const cells = (rows[i] || []).map(low);
    const hasClient = cells.some((c) => c === "client" || c.startsWith("client"));
    const hasBill = cells.some((c) => c.includes("billab") || c.includes("billed"));
    if (hasClient && hasBill) return i;
  }
  return -1;
}

function scoreHeader(headerCells) {
  const cells = (headerCells || []).map(low);
  let s = 0;
  ["client", "billab", "billed", "month", "status", "scope", "code"].forEach((kw) => {
    if (cells.some((c) => c.includes(kw))) s++;
  });
  return s;
}

/**
 * Parse a workbook (ArrayBuffer / Uint8Array) into the dashboard data object.
 * @returns {{records: Array, months: string[], periodLabel: string, sheetName: string}}
 */
export function parseWorkbook(buffer) {
  // Read dates as raw serial numbers (not Date objects): SSF.parse_date_code
  // is timezone-independent, avoiding the off-by-one month shift that
  // cellDates can introduce in negative-UTC timezones.
  const wb = XLSX.read(buffer, { type: "array" });
  const picked = pickSheet(wb);
  if (!picked) {
    throw new Error(
      "Could not find a deployment sheet. Expected a sheet (e.g. \"00 Spend.pro\") with Client / Billability / Billed columns."
    );
  }
  const { rows, headerRow, name } = picked;
  const headers = rows[headerRow] || [];

  // map columns
  const col = {
    client: findCol(headers, (h) => h === "client" || (h.includes("client") && !h.includes("poc"))),
    billability: findCol(headers, (h) => h.includes("billab")),
    hours: findCol(headers, (h) => h.includes("billed") && h.includes("hour")),
    days: findCol(headers, (h) => h.includes("billed") && h.includes("day")),
    month: findCol(headers, (h) => h.includes("month")),
    code: findCol(headers, (h) => h.includes("billing code")),
    billedBy: findCol(headers, (h) => h.includes("billed by") || h === "billed by"),
    status: findCol(headers, (h) => h.includes("status")),
    start: findCol(headers, (h) => h.includes("start")),
    commit: findCol(headers, (h) => h.includes("commit")),
    compl: findCol(headers, (h) => h.includes("complet")),
    kpsLead: findCol(headers, (h) => h.includes("lead")),
    kpsTeam: findCol(headers, (h) => h.includes("team")),
    comments: findCol(headers, (h) => h.includes("comment")),
  };
  // fallback for hours/days/code if "billed" prefix absent
  if (col.hours < 0) col.hours = findCol(headers, (h) => h.includes("hour"));
  if (col.days < 0) col.days = findCol(headers, (h) => h === "days" || h.includes("day"));
  if (col.code < 0) col.code = findCol(headers, (h) => h.includes("code"));

  // scope columns
  const scopeCols = KOS_SCOPE_DEFS.map((def) => ({
    key: def.key,
    idx: findCol(headers, (h) => def.test(h)),
  }));

  if (col.client < 0) throw new Error("Sheet is missing a 'Client' column.");

  const records = [];
  for (let i = headerRow + 1; i < rows.length; i++) {
    const row = rows[i] || [];
    const client = norm(row[col.client]);
    if (!client) continue; // skip blank rows
    // skip obvious total/footer rows
    if (/^(total|grand total|sum)\b/i.test(client)) continue;

    const billedByList = col.billedBy >= 0 ? splitPeople(row[col.billedBy]) : [];
    const scopes = scopeCols
      .filter((s) => s.idx >= 0 && isYes(row[s.idx]))
      .map((s) => s.key);

    const hours = col.hours >= 0 ? toNumber(row[col.hours]) : 0;
    const num = records.length + 1;
    records.push({
      id: "SP-" + String(num).padStart(3, "0"),
      num,
      client,
      billability: col.billability >= 0 ? canonBillability(row[col.billability]) : "None",
      hours,
      days: col.days >= 0 ? toNumber(row[col.days]) : 0,
      monthKey: col.month >= 0 ? toMonthKey(row[col.month]) : null,
      code: col.code >= 0 ? norm(row[col.code]) : "",
      billedBy: billedByList.join(", "),
      billedByList,
      scopes,
      status: canonStatus(col.status >= 0 ? row[col.status] : ""),
      start: col.start >= 0 ? norm(row[col.start]) || null : null,
      commit: col.commit >= 0 ? norm(row[col.commit]) || null : null,
      compl: col.compl >= 0 ? norm(row[col.compl]) || null : null,
      kpsLead: col.kpsLead >= 0 ? norm(row[col.kpsLead]) : "",
      kpsTeam: col.kpsTeam >= 0 ? norm(row[col.kpsTeam]) : "",
      comments: col.comments >= 0 ? norm(row[col.comments]) : "",
    });
  }

  if (!records.length) {
    throw new Error("No deployment rows were found below the header.");
  }

  const months = buildMonthRange(records);
  const periodLabel =
    months.length ? `${months[0]} – ${months[months.length - 1]}` : "—";

  return { records, months, periodLabel, sheetName: name };
}

/* Build a continuous YYYY-MM axis spanning the data (nice trend chart). */
export function buildMonthRange(records) {
  const keys = records.map((r) => r.monthKey).filter(Boolean).sort();
  if (!keys.length) return [];
  const [minY, minM] = keys[0].split("-").map(Number);
  const [maxY, maxM] = keys[keys.length - 1].split("-").map(Number);
  const out = [];
  let y = minY, m = minM;
  while (y < maxY || (y === maxY && m <= maxM)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m++;
    if (m > 12) { m = 1; y++; }
    if (out.length > 600) break; // safety
  }
  return out;
}

/** Convenience: read a File/Blob and parse it. */
export async function parseExcelFile(file) {
  const buffer = await file.arrayBuffer();
  return parseWorkbook(buffer);
}
